<template>
  <div class="page-wrap">
    <div class="page-inner-wrap">
      <close-btn :moduleName="moduleName"></close-btn>
      <page-title
        :moduleName="moduleName"
        :title="getText('searchTitle')"
      ></page-title>
      <article-banner
        :banner="getIco('listbanner')"
        class="banner"
      ></article-banner>
      <div class="search">
        <input
          :placeholder="getText('searchTips')"
          class="search-input"
          id="searchInput"
          type="text"
          v-model="searchContent"
          @blur="handleSearchArticle"
          @focus="initViewport"
        />
        <div
          class="ico"
          @click="handleSearchArticle"
          :style="getIcoOfBg('searchbtn')"
        ></div>
      </div>
      <article-tag
        :list="tagList"
        :activeId="activeTagId"
        @chooseTag="chooseTag"
      ></article-tag>
      <article-lists
        class="article-lists"
        v-if="filterArticleList.length"
        :articleList="filterArticleList"
        :showLikes="true"
        :source="pageSourceType.searchResult"
      ></article-lists>
      <p v-else-if="isSearch" class="no-tips">{{ getText("noResult") }}</p>
    </div>
    <nav-bar></nav-bar>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onActivated, watch } from "vue";
import PageTitle from "@components/common/PageTitle.vue";
import CloseBtn from "@components/common/CloseBtn.vue";
import ArticleBanner from "@components/article/ArticleBanner.vue";
import ArticleLists from "@components/article/ArticleLists.vue";
import NavBar from "@components/common/NavBar.vue";
import ArticleTag from "@components/article/ArticleTag.vue";
import { getIco, getText, getIcoOfBg } from "@config/siteConfig";
import { initViewport } from "@utiles/index";
import useModule from "@hooks/useModule";
import useLoading from "@hooks/useLoading";
import { useBaseStore } from "@store/base";
import { searchArticle } from "@api/article";
import { useArticleStore } from "@store/article";
import { ArticleSearchType } from "@config/article";
import usePointViewPage from "@hooks/usePointViewPage";
const article = useArticleStore();

const isSearchTag = computed(() => {
  return article.searchTag.text && article.searchTag.id;
});

const isSearch = ref(false);

const { moduleName } = useModule();
const { setLoadingState } = useLoading();
setLoadingState(false);
usePointViewPage({ pageKey: pageType.searchPage, moduleName });

const base = useBaseStore();
const searchContent = ref("");
const articleList = ref([]);
const filterArticleList = ref([]);
const tagList = ref([]);
const activeTagId = ref("");

watch(
  [articleList, activeTagId],
  ([newList, newActiveTagId]) => {
    if (!newActiveTagId) {
      filterArticleList.value = newList;
      return;
    }
    const newLists = newList.filter((item) => {
      return item.WEBSITE_TAG_ID.split(",").includes(String(newActiveTagId));
    });
    filterArticleList.value = newLists;
  },
  { immediate: true, deep: true }
);

const chooseTag = (id) => {
  activeTagId.value = id;
  clickButtonPoint({
    btnName: "tags",
    moduleName: moduleName,
    otherParams: {
      tagId: id,
    },
  });
};

const resetSearchInfo = () => {
  searchContent.value = "";
  articleList.value = [];
  tagList.value = [];
  activeTagId.value = "";
  isSearch.value = false;
};
const handleSearchArticle = () => {
  activeTagId.value = "";
  articleList.value = [];
  tagList.value = [];
  isSearch.value = false;
  if (!searchContent.value) {
    return;
  }
  setLoadingState(true);
  clickButtonPoint({
    btnName: "search",
    moduleName: moduleName,
    otherParams: {
      content: searchContent.value,
    },
  });
  searchArticle({
    name: base.name,
    language: base.lang,
    search: searchContent.value,
    searchType: isSearchTag.value
      ? ArticleSearchType.Tag
      : ArticleSearchType.TagAndTitle,
    preview: base.preview,
    sign: base.sign,
  }).then((res) => {
    window.officialGuideDataLayer.push({
      eventName: "search",
      eventType: isSearchTag.value ? "auto" : "manual",
      eventReason: searchContent.value,
    });
    isSearch.value = true;
    article.setSeatchTag({
      text: "",
      id: "",
    });
    if (res?.result == 1 && res?.data) {
      articleList.value = res.data?.articleList || [];
      const tagListApi = res.data?.tagList;
      if (tagListApi && Array.isArray(tagListApi) && tagListApi.length > 0) {
        tagList.value = tagListApi.map((item) => {
          return {
            CONTENT: JSON.parse(item.CONTENT || "{}")[base.lang] || "",
            ID: item.ID,
            NAME: item.NAME,
          };
        });
      } else {
        tagList.value = [];
      }
      setLoadingState(false);
    }
  });
};

onActivated(() => {
  document.getElementById('searchInput')?.focus();
  if (isSearchTag.value) {
    searchContent.value = article.searchTag.text;
    handleSearchArticle();
  } else {
    resetSearchInfo();
  }
});
</script>

<style lang="scss" scoped>
@import "@assets/scss/dimen.scss";
@import "@assets/scss/mixin.scss";
.search {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 90px;
  border-radius: 14px;
  padding: 0 20px;
  box-sizing: border-box;
  background: $layercolor;
  margin: 30px auto;
  &-input {
    flex: 1;
    padding-right: 20px;
    font-size: $fzNormal;
  }

  .ico {
    flex-shrink: 0;
    display: block;
    width: 55px;
    height: 45px;
    cursor: pointer;
    @include addTouchEffect();
  }
}
.no-tips {
  text-align: center;
}
@media (orientation: landscape) {
  .banner {
    display: none;
  }
}

@media only screen and (min-height: 640px) and (orientation: landscape) {
  .search {
    height: calc(90px / $pcRate);
    border-radius: calc(14px / $pcRate);
    padding: 0 calc(20px / $pcRate);
    margin: calc(30px / $pcRate) auto;
    &-input {
      padding-right: calc(20px / $pcRate);
      font-size: calc($fzNormal / $pcRate);
    }

    .ico {
      width: calc(55px / $pcRate);
      height: calc(45px / $pcRate);
    }
  }
}
</style>
